import { useCallback, useMemo } from 'react';

import { CartItem, useUpdateCartItem } from '@givebacks/api/store';
import { studentKeys } from '@givebacks/api/student';
import { useCartUpdateHandler } from '@givebacks/shop';
import { DeepPartial } from '@givebacks/utils/types';
import { useQueryClient } from '@tanstack/react-query';
import { getErrorMessage_v2 } from 'core/Utils';
import { notifications } from 'ui';

type CartHandlerOptions = Pick<Parameters<typeof useCartUpdateHandler>[0], 'onCartCreate'>;

interface AddFeeToCartHandlerArgs {
  feeId: string;
  causeId: string;

  studentFeeId: string;

  /** The price of the fee to add to the cart */
  price: number;

  cartHandlerOptions?: CartHandlerOptions;
}

/**
 *
 * Handles creating the cart, cart items and all other data structures needed for the shop to properly
 * create or update a cart with a fee.
 *
 */
const useFeeCartHandler = ({
  feeId,
  causeId,
  price,
  studentFeeId,
  cartHandlerOptions,
}: AddFeeToCartHandlerArgs) => {
  const queryClient = useQueryClient();
  const { mutate: updateCartItem, isPending: isUpdateCartItemLoading } = useUpdateCartItem();

  const cartItems: DeepPartial<CartItem>[] = useMemo(() => {
    return [
      {
        store_item_id: feeId,
        quantity: 1,
        price,
        student_fee_id: studentFeeId,
      },
    ];
  }, [feeId, price, studentFeeId]);

  const { handleCartUpdate, isLoading: isCartUpdateLoading } = useCartUpdateHandler({
    storeId: causeId,
    storeItemId: feeId,
    cartFields: {},
    cartItems,
    quantity: 1,
    onCartCreate: () => {
      cartHandlerOptions?.onCartCreate?.();
    },
    ...cartHandlerOptions,
  });

  /**Handler to update an existing cart item, requires a cartId and cartItemId to be passed in at call time */
  const handleUpdateExistingCartItem = useCallback(
    ({ cartId, cartItemId }: { cartId: string; cartItemId: string }) => {
      updateCartItem(
        {
          cartId: cartId,
          cartItemId: cartItemId,
          cart_item: { price },
        },
        {
          onSuccess: () => {
            notifications.show({ message: 'Cart item updated', variant: 'success' });
            cartHandlerOptions?.onCartCreate?.();
          },
          onError: (error) => {
            notifications.show({ message: getErrorMessage_v2(error), variant: 'error' });
          },
        }
      );
    },
    [cartHandlerOptions, price, updateCartItem]
  );

  const invalidateStudentFee = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: studentKeys.id('user').consumer.all,
    });
  }, [queryClient]);

  const isLoading = useMemo(() => {
    return isUpdateCartItemLoading || isCartUpdateLoading;
  }, [isUpdateCartItemLoading, isCartUpdateLoading]);

  return {
    handleCartUpdate,
    handleUpdateExistingCartItem,
    isLoading,
    cartItems,
    invalidateStudentFee,
  };
};

export { useFeeCartHandler };
